// Static-aura recompute pipeline + aura/site placement snapping,
// extracted from GameBoard.jsx. recomputeStaticAuras is the single
// full-board pass that re-derives every continuous effect (stat deltas,
// keyword grants, ward grants, control claims, conditional grants,
// equipment auras) from the ledger + on-table sources; the snap helpers
// keep auras glued to their anchors and sites to their owner edge.
//
// Host contract: meshes, state, effectLedger, playerResources, sync,
// lifeHUDs, _cellAtWorld, _getFullCard, _broadcast* primitives,
// rebuildStatusBadges, _refreshLifeHUD/_refreshMovementHUD. Pure scope
// resolution stays in engine/automation (resolveAuraScope, cellsInScope).

import { animateCardTap } from '../../../utils/game/animations';
import { resolveAvatarRules } from '../../../utils/game/avatarRules';
import { CARD_THICKNESS, updateLifeHUD, updateLifeHUDOrientation } from '../../../utils/game/cardMesh';
import { OP, diffConditionalGrants, diffControlClaims, isDisabled, snapshotControlClaims } from '../../../utils/game/engine/automation';
import { deriveStaticAuras, applyDerivedToCard } from '../../../utils/game/engine/automation/staticAuraDerive';
import { dispatchSilentOp, SILENT_OP_SET } from '../../../utils/game/engine/automation/ops/silentOps';
import { AURA_SHAPE, getAuraShape, hasAuraFootprint, isAutomatonCard, legalAura2Anchors, nearestAura2Anchor, nearestAura4Anchor, sampleCellGeometry, siteSnapPoint } from '../../../utils/game/engine/core';
import { isCardSilenced } from '../../../utils/game/engine/core/keywordQuery';
import { computeGridPositions } from '../../../utils/game/grid';
import { moveKinematicBody } from '../../../utils/game/physicsWorld';
import { sameControllerSiteInCell } from '../../../utils/game/engine/automation/ops/cards/babelTower';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { cardLogLabel } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';
import { SET_CARD_STATS } from '../../../utils/game/net/matchCore';
import { sendMatchCommand } from '../../../utils/game/socketClient';

// Fire an equipped artifact's automation entries. Called from
// equipCardTo right after the artifact attaches to a host. Uses the
// HOST's controller (which may differ from the artifact's printed
// ownership). Each ledger entry references the artifact instance by
// id so cleanup on detach is simple.
export function applyEquippedArtifactEffects(host, equippedCard, hostCard) {
  const fullEquip = host._getFullCard(equippedCard.cardId);
  if (!fullEquip?.automation?.length) return;
  if (fullEquip.type !== 'Artifact') return;

  const hostController = hostCard.rotated ? 'p2' : 'p1';
  let manaDelta = 0;

  for (const entry of fullEquip.automation) {
    // While-equipped lifecycle: only continuous (static) and on-play
    // (treated as on-equip for artifacts) effects apply. on_turn_start
    // would fire from the per-turn scan below.
    if (entry.trigger !== 'on_play' && entry.trigger !== 'static') continue;
    if (!host._evaluateCondition(entry.condition, equippedCard, fullEquip)) continue;

    for (const effect of entry.effect) {
      if (effect.op !== OP.GRANT_THRESHOLD && effect.op !== OP.GRANT_MANA) continue;

      const ledgerEntry = {
        sourceCardId: equippedCard.id,
        sourceCardName: fullEquip.name || equippedCard.name,
        controller: hostController,
        effect,
        expiry: { type: 'until_leaves' },
      };
      host.effectLedger.add(ledgerEntry);
      if (effect.op === OP.GRANT_MANA) manaDelta += effect.args?.amount || 0;
    }
  }

  if (manaDelta > 0) host.playerResources.grantMana(hostController, manaDelta);

  // Silent-set Genesis ops on attachable artifacts resolve HERE, at
  // attach time, anchored on the HOST card. Not at drop time, where
  // a Monument hasn't picked its site yet. The play-time pipeline
  // skips these entries and stamps `_pendingFieldGenesis`; consuming
  // the flag here keeps Genesis once-per-realm-entry. A later
  // MANUAL re-equip of an artifact that already resolved its Genesis
  // (on the field or on another host) must not re-fire it. Shrine of
  // the Dragonlord: "Genesis → Teleport all Dragons here" pulls them
  // to the site the Shrine was conjured atop.
  if (equippedCard._pendingFieldGenesis) {
    delete equippedCard._pendingFieldGenesis;
    for (const entry of fullEquip.automation) {
      if (entry.trigger !== 'on_play') continue;
      if (!(entry.effect || []).every((e) => SILENT_OP_SET.has(e.op))) continue;
      if (!host._evaluateCondition(entry.condition, equippedCard, fullEquip)) continue;
      for (const effect of entry.effect) {
        dispatchSilentOp(effect, equippedCard, entry, host._silentOpsHost, {
          controller: hostController,
          anchorCard: hostCard,
        });
      }
    }
  }

  host.recalculateSiteAffinities(hostController);
  host.forceUpdate();
}

// Remove ledger entries for a piece of equipment that just detached
// (host left play, or manual detach). Pairs with
// _applyEquippedArtifactEffects above.
export function clearEquippedArtifactEffects(host, equipmentId) {
  if (!host.effectLedger.entries.some((e) => e.sourceCardId === equipmentId)) return;
  // v1.20. Reverse the mana bump this artifact contributed. Onyx
  // Core et al. bump mana on equip via _applyEquippedArtifactEffects;
  // unequip needs the symmetric subtract. recalculateSiteAffinities
  // only handles thresholds.
  host._reverseManaContribution(equipmentId);
  host.effectLedger.removeBySource(equipmentId);
  host.recalculateSiteAffinities('p1');
  host.recalculateSiteAffinities('p2');
}

// Sum every GRANT_MANA entry sourced from `sourceCardId` and subtract
// those amounts from the matching controllers' mana counters. Called
// BEFORE removeBySource so the entries are still in the ledger.
// Cap at 0. Broadcasts the new mana totals.
export function reverseManaContribution(host, sourceCardId) {
  const drops = { p1: 0, p2: 0 };
  for (const entry of host.effectLedger.entries) {
    if (entry.sourceCardId !== sourceCardId) continue;
    if (entry.effect?.op !== OP.GRANT_MANA) continue;
    drops[entry.controller] = (drops[entry.controller] || 0) + (entry.effect.args?.amount || 0);
  }
  if (drops.p1 === 0 && drops.p2 === 0) return;
  for (const player of ['p1', 'p2']) {
    if (drops[player] === 0) continue;
    host.playerResources.payMana(player, drops[player]);
  }
}

// Walk every static MODIFY_CAST_COST source (board minions/avatars +
// equipped artifacts) and idempotently install its cost-modifier ledger
// entry. Host-coupled (it owns the EffectLedger), so it stays out of the
// pure derivation: removal happens via removeBySource on source-leave;
// Avatars never leave, so their entry persists for the game (correct).
// `filter.cells: "source_cell"` resolves to the source's live cell.
function installStaticCostModifiers(host) {
  const installFrom = (sourceCard, fullSource, overrides) => {
    if (isCardSilenced(sourceCard) || isDisabled(sourceCard)) return;
    const baseEntries = fullSource?.automation || [];
    const avatarExtras = sourceCard?.type === 'Avatar'
      ? (resolveAvatarRules(sourceCard).staticAuras || [])
      : [];
    const entries = avatarExtras.length > 0 ? [...baseEntries, ...avatarExtras] : baseEntries;
    for (const entry of entries) {
      if (entry.trigger !== 'static') continue;
      for (const effect of (entry.effect || [])) {
        if (effect.op !== OP.MODIFY_CAST_COST) continue;
        const controller = overrides?.controllerRotated !== undefined
          ? (overrides.controllerRotated ? 'p2' : 'p1')
          : (sourceCard.rotated ? 'p2' : 'p1');
        const target = effect.args?.target_player === 'opponent'
          ? (controller === 'p1' ? 'p2' : 'p1')
          : controller;
        let installEffect = effect;
        if (effect.args?.filter?.cells === 'source_cell') {
          const mesh = host.meshes.get(sourceCard.id);
          const cell = mesh && host._cellAtWorld(mesh.position.x, mesh.position.z);
          if (!cell) continue;
          installEffect = {
            ...effect,
            args: { ...effect.args, filter: { ...effect.args.filter, cells: [{ col: cell.col, row: cell.row }] } },
          };
        }
        const alreadyInstalled = host.effectLedger.entries.some((e) =>
          e.sourceCardId === sourceCard.id
          && e.effect.op === OP.MODIFY_CAST_COST
          && e.targetPlayer === target);
        if (!alreadyInstalled) {
          host.effectLedger.add({
            sourceCardId: sourceCard.id,
            sourceCardName: sourceCard.name || '',
            controller,
            targetPlayer: target,
            effect: installEffect,
            expiry: { type: 'until_leaves' },
          });
        }
      }
    }
  };

  for (const [, sourceMesh] of host.meshes) {
    const sourceCard = sourceMesh.userData.cardInstance;
    if (!sourceCard || isDisabled(sourceCard)) continue;
    const fullSource = host._getFullCard(sourceCard.cardId);
    if (fullSource?.type === 'Artifact' && !isAutomatonCard(fullSource)) continue;
    installFrom(sourceCard, fullSource);
  }
  for (const [, hostMesh] of host.meshes) {
    const hostCard = hostMesh.userData.cardInstance;
    const equipment = hostCard?.equippedCards;
    if (!equipment || equipment.length === 0 || isDisabled(hostCard)) continue;
    for (const equipped of equipment) {
      const fullEquip = host._getFullCard(equipped.cardId);
      if (fullEquip?.type !== 'Artifact') continue;
      installFrom(equipped, fullEquip, { controllerRotated: hostCard.rotated, bearerCard: hostCard });
    }
  }
}

// Recompute every card's _auraStatDelta from current board state and
// refresh the affected ATK lifeHUDs. Cheap enough to call on every
// mutation; no need to be smart about which cards changed.
//
// The per-card stat/keyword/threshold/subtype/alias/granted-trigger
// DERIVATION is the pure `deriveStaticAuras` engine module (the same
// function the server runs as a post-reduce hook to make these fields
// authoritative). This host wrapper builds the board view, applies the
// derived bag onto each live card, and drives the side-effects the pure
// half only DESCRIBES: conditional-grant ledger diffs, MODIFY_CAST_COST
// install, control-flip animation, ward badges, self-dispel, HUD/badge
// refresh.
export function recomputeStaticAuras(host) {
  // Snapshot old aura status sets so we only rebuild status badges
  // for cards whose aura keyword set actually changed.
  const oldAuraStatuses = new Map();
  // Snapshot the existing control claim map (v1.21). Any card no
  // longer in the new claim map (built below) reverts to its
  // _origRotated on this pass. Helper lives in control.js.
  const oldControlClaims = snapshotControlClaims(host.meshes);

  for (const [, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    if (!card) continue;
    oldAuraStatuses.set(card.id, new Set(card._auraStatuses || []));
  }

  // Pure derivation: returns per-card derived bags + the cross-card
  // side-effect descriptors (control claims, conditional grants, ward
  // grants, self-dispel set). The board view is host.meshes in the
  // { meshes, cellAt, getFullCard } scope shape.
  const { derived, controlClaims: newControlClaims, wantedConditionalGrants, wardGrantTargetIds, pendingSelfDispels } =
    deriveStaticAuras(
      { meshes: host.meshes, cellAt: host._cellAtWorld, getFullCard: host._getFullCard },
      {
        currentTurn: host.state.currentTurn,
        evaluateCondition: (condition, sourceCard, fullCard, extraOpts) =>
          host._evaluateCondition(condition, sourceCard, fullCard, extraOpts),
        resolveAvatarRules,
      },
    );

  // Write the derived continuous-effect fields onto every live card.
  for (const [, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    if (!card) continue;
    applyDerivedToCard(card, derived.get(card.id));
  }

  // MODIFY_CAST_COST install (Templar). Side-effecting ledger write the
  // pure half intentionally leaves to the host: idempotent install per
  // source/target, removed via removeBySource on source-leave. Walks the
  // same board sources the pure pass did so the cell-resolved variant
  // (Dragonlord's Lair) reads the live mesh position.
  //
  // Auth path (W3): the SERVER derives these entries (auraDerive
  // applyDerivedLedger) and they arrive via the effectLedger fold. Installing
  // them here too made the client a second writer the fold clobbered every
  // diff. The server breakdown prices the cast debit off the same entries.
  if (!host._authZonesEnabled?.()) installStaticCostModifiers(host);

  // Apply any DISPEL_SELF_IF_SCOPE_EMPTY hits collected during the
  // apply loop. sendCardToPile is the canonical dispel sink; its
  // _disposeCardEffectsOnLeave chain calls recomputeStaticAuras
  // again so the freshly-dispelled source's keyword/stat grants
  // are stripped from the board on the next pass. We re-resolve
  // the card from `host.meshes` because an earlier dispel this
  // same pass could have already removed it.
  if (pendingSelfDispels.size > 0) {
    for (const cardId of pendingSelfDispels) {
      const mesh = host.meshes.get(cardId);
      const card = mesh?.userData?.cardInstance;
      if (!card) continue;
      host._silentOpsHost.logAction('{card} dispels. No targets in scope', { card: cardLogLabel(card, msg('Aura')) });
      host._silentOpsHost.sendToCemetery(card);
    }
  }

  // Refresh ATK and HP HUDs. In Sorcery "+N power" buffs the
  // combined attack/defense stat line, so the aura delta flows
  // into both halves.
  for (const [cardId, hud] of host.lifeHUDs) {
    const mesh = host.meshes.get(cardId);
    const card = mesh?.userData?.cardInstance;
    if (!card || !hud?.sprite) continue;
    const auraDelta = card._auraStatDelta || 0;
    const atkDisplay = (card.currentAttack ?? 0) + auraDelta;
    updateLifeHUD(hud.sprite, atkDisplay, 'atk', atkDisplay - (card.baseAttack ?? atkDisplay));
    if (hud.hpSprite) {
      // Floor at 0. See DAMAGE_TARGET handler for why currentLife
      // can go negative (Sorcery "Zero" rule for 0-power minions).
      const hpDisplay = Math.max(0, card.currentLife ?? 0) + auraDelta;
      updateLifeHUD(hud.hpSprite, hpDisplay, 'hp', hpDisplay - (card.baseLife ?? hpDisplay));
    }
  }

  // Reconcile conditional grant ledger entries (The Empyrean et al.)
  // Diff lives in cardEffects/conditional.js. GameBoard applies the
  // add/remove ops with sync calls and triggers a site-affinity
  // recalc if anything mutated.
  //
  // Auth path (W3): the SERVER owns the derived-static ledger diff (auraDerive
  // applyDerivedLedger). The folded ledger already CONTAINS these entries, so
  // the local diff (which the fold clobbered every diff and the client re-added,
  // a standing second-writer tension) is gated off entirely.
  if (!host._authZonesEnabled?.()) {
    const { toAdd, toRemove, mutated } = diffConditionalGrants(
      wantedConditionalGrants,
      host.effectLedger.entries,
    );
    if (toRemove.length > 0) {
      host.effectLedger.entries = host.effectLedger.entries.filter((e) => !toRemove.includes(e));
    }
    for (const entry of toAdd) {
      host.effectLedger.add(entry);
    }
    if (mutated) {
      host.recalculateSiteAffinities('p1');
      host.recalculateSiteAffinities('p2');
    }
  }
  // Aura-driven threshold mutations (Drought / Atlantean Fate
  // suppression, Sow the Earth multiplier) write directly onto site
  // cards rather than through the conditional-grant ledger, so the
  // `mutated` flag above won't catch them. Always refresh the HUD
  // affinity tracker at the end of recompute. Cost is small and
  // it keeps the tracker in lockstep with the playmat pips.
  host.recalculateSiteAffinities('p1');
  host.recalculateSiteAffinities('p2');

  // Rebuild status badges only for cards whose aura set churned.
  // badge mesh rebuilds are heavier (geometry + textures).
  for (const [cardId, oldSet] of oldAuraStatuses) {
    const mesh = host.meshes.get(cardId);
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    const newSet = new Set(card._auraStatuses || []);
    let changed = oldSet.size !== newSet.size;
    if (!changed) {
      for (const k of newSet) if (!oldSet.has(k)) { changed = true; break; }
    }
    if (changed) host.rebuildStatusBadges(cardId);
  }

  // Apply control-claim deltas. The diff (and the _origRotated /
  // _controlClaimedBy bookkeeping) lives in cardEffects/control.js;
  // GameBoard just handles the per-op animation + sync.
  //
  // Auth path: the SERVER applies grant_control claims (auraDerive
  // applyControlClaims). The flip arrives in the mirror and folds through
  // applyCardStateFromMirror → _applyControllerFlip. Applying it here too made
  // the client a second writer (SET_CONTROLLER emission racing the server's
  // own claim), so the whole local claim application is gated off.
  if (!host._authZonesEnabled?.()) {
    const getCard = (id) => host.meshes.get(id)?.userData?.cardInstance || null;
    const controlOps = diffControlClaims(oldControlClaims, newControlClaims, getCard);
    for (const op of controlOps) {
      const mesh = host.meshes.get(op.cardId);
      const card = getCard(op.cardId);
      if (!card || !mesh) continue;
      card.rotated = op.newRotated;
      host._applyOwnershipFlip(card, mesh);
    }
  }

  // v1.27. Push Ward TOKEN grants live: badges + status broadcast
  // for every card that received a fresh token from an aura this pass.
  // Auth path: statuses are server-derived and fold back via the mirror, so
  // the local broadcast is gated off like the claim/threshold paths above.
  for (const cardId of wardGrantTargetIds) {
    host.rebuildStatusBadges(cardId);
    if (!host._authZonesEnabled?.()) host._broadcastCardStatus(cardId, 'ward', true);
  }

  // v1.25. Auras shifted (lost +N power buffs, etc.) may have
  // pushed a minion under the damage threshold. Sweep before returning.
  // The sweep's re-entry guard prevents an infinite recompute loop
  // when a cascading death triggers another recompute.
  host._sweepDeadMinions();

  // Re-derive every owner-locked unit hand's controller from its owner card's
  // control. This single choke point covers all three control-change paths
  // (manual flip, continuous "controls all X" claim above, inbound ownership
  // sync) on both clients, so control of the hand always tracks control of the
  // card and can never split-brain. See handState.reconcileUnitHandControllers.
  // When control actually changed, re-broadcast the hands: the sanitize is
  // per-recipient, so the seat that just GAINED control must receive the now-
  // entitled faces (without this they keep the old all-backs view and can't see
  // or play the hand). Broadcasting only on change keeps it from looping. Once
  // both clients agree, reconcile returns false and nothing is re-sent.
  if (host._reconcileUnitHandControllers?.()) host._syncUnitHands?.();

  // P3-auras: the server now DERIVES the continuous-effect fields
  // (_auraStatDelta / _auraStatuses / _auraSuppressedKeywords) itself via the
  // shared deriveStaticAuras hook, so the client no longer EMITS them (that was
  // the SET_CARD_STATS{auraStatDelta} anti-cheat hole. A tampered client could
  // claim a bogus delta combat trusted verbatim). The fields ride back down on
  // the next match:diff and are folded by applyCardStateFromMirror.
  //
  // What stays client-authored are the server's INPUTS it can't derive: the
  // PRINTED keywords (parsed client-side at spawn from card identity) and the
  // player's MANUAL radial suppressions. The server reads these for its synced
  // hasKeyword / isCardSilenced, then derives the aura fields ON TOP of them.
  // Auth path only; a persistent last-emitted cache fires once on spawn / once
  // on a radial toggle and never loops once the server already has the value.
  if (host._authZonesEnabled?.()) {
    if (!host._lastKwEmit) host._lastKwEmit = new Map();
    const cache = host._lastKwEmit;
    const arrEq = (a = [], b = []) => {
      if (a.length !== b.length) return false;
      const s = new Set(a);
      return b.every((x) => s.has(x));
    };
    for (const [, mesh] of host.meshes) {
      const card = mesh.userData.cardInstance;
      if (!card) continue;
      const cur = {
        printed: card._printedKeywords || [],
        suppressed: card._suppressedKeywords || [],
      };
      const prev = cache.get(card.id) || {};
      const payload = { cardId: card.id };
      if (!arrEq(prev.printed, cur.printed)) payload.printedKeywords = [...cur.printed];
      if (!arrEq(prev.suppressed, cur.suppressed)) payload.suppressedKeywords = [...cur.suppressed];
      if (Object.keys(payload).length > 1) {
        sendMatchCommand(host._matchKey(), { type: SET_CARD_STATS, payload });
        cache.set(card.id, cur);
      }
    }
  }
}

// Apply an ownership change to a card: animate the 180° flip,
// refresh the ownership ring + ATK/HP pill orientation, broadcast
// the switch, refresh the hover overlay. Reuses the same primitives
// as the manual switchCardOwnership menu action. Used by the
// GRANT_CONTROL pass in recomputeStaticAuras (v1.21).
export function applyOwnershipFlip(host, card, mesh) {
  animateCardTap(mesh, card);
  host.applyOwnershipOutline(mesh);
  updateLifeHUDOrientation(host.lifeHUDs.get(card.id), card, host.state.isHost);
  host._broadcastOwnership?.(card.id, card.rotated);
  if (host.hoveredMesh === mesh) host.syncHoverStatusOverlay();
  // v1.107. Sites are anchored to the controller's near edge of
  // their cell, so a control flip moves the snap target to the
  // opposite edge. Re-snap locally; the peer's card:ownership
  // handler runs the same snap from the broadcasted rotated flag,
  // so both sides land at the same point without an extra wire msg.
  if (card.isSite) host._snapSiteToOwnerEdge(card, mesh);
}

// v1.107. Compute the owner-relative snap point for the cell the
// site currently occupies and apply it to mesh + cardInstance. Pure
// siteSnapPoint picks the bottom-center for the controller's
// perspective (P1: world bl/br midpoint, P2: world tl/tr midpoint).
// Both peers derive the same point from card.rotated, so calling
// this on each side after a control flip keeps the renders in sync
// without a position broadcast. Returns true when the mesh moved.
// Aura snap. Two of the three shapes need positional snap because the
// physical card has to sit at a precise sub-cell anchor:
//   INTERSECTION_4. Corner shared by 4 cells (no cell has a natural
//     "this is where the aura goes" point).
//   INTERSECTION_2. Midpoint of an edge between two sites.
//
// ATOP_SITE auras (Wildfire, Castle's Ablaze, etc.) cover a single
// cell. The cell IS the anchor. We don't need to glue the card to
// the underlying site mesh's exact center. Treating it like a regular
// single-cell card placement (drop where the cursor was, leave it
// there) keeps the aura visually independent of the site card and
// matches how every other card on a cell behaves.
//
// Drop legality for ATOP_SITE is already enforced by the reach set
// (`entry.cells` only contains legal site cells, the drop gate
// rejects illegal drops back to hand).
export function snapAuraToAnchor(host, card, mesh) {
  // An Enchantress-animated aura is type 'Minion' but still carries its
  // aura footprint (`_auraShape`); it must keep re-anchoring as it moves
  // so its occupied cells stay coherent (combat reach reads the anchor).
  if (!mesh || !hasAuraFootprint(card)) return null;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return null;
  const shape = card._auraShape || getAuraShape(card);
  if (shape.shape === AURA_SHAPE.ATOP_SITE || shape.shape === AURA_SHAPE.ATOP_VOID) {
    // No-snap branch. Still stamp the cell-keyed anchor so static
    // aura scope resolution ('covered') can find it.
    const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (cell) card._auraAnchor = { col: cell.col, row: cell.row };
    return null;
  }
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const dropX = mesh.position.x;
  const dropZ = mesh.position.z;

  let anchor = null;
  if (shape.shape === AURA_SHAPE.INTERSECTION_4) {
    anchor = nearestAura4Anchor(grid, totalCols, totalRows, dropX, dropZ);
  } else if (shape.shape === AURA_SHAPE.INTERSECTION_2) {
    const legal = legalAura2Anchors(host.meshes, !!card.rotated, host._cellAtWorld, grid);
    anchor = nearestAura2Anchor(legal, dropX, dropZ);
  }
  if (!anchor) return null;

  mesh.position.x = anchor.point.x;
  mesh.position.z = anchor.point.z;
  card.x = anchor.point.x;
  card.z = anchor.point.z;
  card._auraAnchor = anchor.edge
    ? { col: anchor.col, row: anchor.row, edge: anchor.edge }
    : { col: anchor.col, row: anchor.row };
  const body = mesh.userData?.body;
  if (body) moveKinematicBody(body, anchor.point.x, mesh.position.y, anchor.point.z);
  return anchor;
}

export function snapSiteToOwnerEdge(host, card, mesh) {
  if (!card?.isSite || !mesh) return false;
  const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!cell) return false;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return false;
  const geo = sampleCellGeometry(grid, cell.col, cell.row);
  if (!geo) return false;
  const snap = siteSnapPoint(geo, !!card.rotated);
  if (!snap) return false;
  // The Tower of Babel: The Apex of Babel shares its Base's cell, so lift it
  // one card-thickness above the table to read as a tower instead of
  // z-fighting the coplanar Base beneath it.
  const liftY = card.name === 'The Apex of Babel'
    && sameControllerSiteInCell('The Base of Babel', card, host.meshes, host._cellAtWorld)
    ? CARD_THICKNESS : 0;
  const settled = Math.abs(mesh.position.x - snap.x) < 0.001
    && Math.abs(mesh.position.z - snap.z) < 0.001
    && Math.abs(mesh.position.y - liftY) < 0.001;
  if (settled) return false;
  mesh.position.x = snap.x;
  mesh.position.z = snap.z;
  mesh.position.y = liftY;
  card.x = snap.x;
  card.z = snap.z;
  // Keep the kinematic physics body in sync with the visual mesh.
  // Without this the next physics tick yanks the mesh back to the
  // body's stale (pre-snap) position via the rigid-body coupling.
  const body = mesh.userData?.body;
  if (body) moveKinematicBody(body, snap.x, mesh.position.y, snap.z);
  return true;
}
